import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  LocalizedInput,
  Modal,
  Select,
  formatSum,
  toSum,
  toTiyin,
  type Localized,
} from '@/components/ui';

interface Accessory {
  id: string;
  name: Localized;
  price: number;
  priceMode: 'PER_DAY' | 'FLAT';
  depositAmount: number | null;
  quantityTotal: number;
  isActive: boolean;
  _count: { products: number };
}

interface Draft {
  id?: string;
  name: Localized;
  price: string;
  priceMode: 'PER_DAY' | 'FLAT';
  depositAmount: string;
  quantityTotal: string;
}

const EMPTY: Draft = {
  name: {},
  price: '',
  priceMode: 'PER_DAY',
  depositAmount: '',
  quantityTotal: '0',
};

export default function AccessoriesPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['accessories'],
    queryFn: async () => (await api.get<Accessory[]>('/admin/accessories')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['accessories'] });
  const fail = (e: unknown) => setError(errorMessage(e));

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const payload = {
        name: value.name,
        price: toTiyin(value.price),
        priceMode: value.priceMode,
        depositAmount: value.depositAmount ? toTiyin(value.depositAmount) : undefined,
        quantityTotal: Number(value.quantityTotal || 0),
      };

      return value.id
        ? api.patch(`/admin/accessories/${value.id}`, payload)
        : api.post('/admin/accessories', payload);
    },
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (item: Accessory) =>
      api.patch(`/admin/accessories/${item.id}`, { isActive: !item.isActive }),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/accessories/${id}`),
    onSuccess: invalidate,
    onError: fail,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Аксессуары</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Пояс, обувь, головной убор, украшения — доплата к аренде товара
          </p>
        </div>
        <Button onClick={() => setDraft({ ...EMPTY })}>Новый аксессуар</Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Card>
        {isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-neutral-500">Аксессуаров пока нет</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Название</th>
                  <th className="pb-2 font-medium">Цена</th>
                  <th className="pb-2 font-medium">Залог</th>
                  <th className="pb-2 font-medium">Остаток</th>
                  <th className="pb-2 font-medium">Товаров</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-t border-neutral-100 dark:border-neutral-800">
                    <td className="py-2">
                      <span className={item.isActive ? '' : 'text-neutral-400 line-through'}>
                        {item.name.ru}
                      </span>
                    </td>
                    <td className="py-2">
                      {formatSum(item.price)}{' '}
                      <Badge>{item.priceMode === 'PER_DAY' ? 'в сутки' : 'за период'}</Badge>
                    </td>
                    <td className="py-2">{formatSum(item.depositAmount)}</td>
                    <td className="py-2">{item.quantityTotal}</td>
                    <td className="py-2 text-neutral-500">{item._count.products}</td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setDraft({
                            id: item.id,
                            name: item.name,
                            price: toSum(item.price),
                            priceMode: item.priceMode,
                            depositAmount: toSum(item.depositAmount),
                            quantityTotal: String(item.quantityTotal),
                          })
                        }
                      >
                        Изм.
                      </Button>
                      <Button variant="ghost" onClick={() => toggle.mutate(item)}>
                        {item.isActive ? 'Скрыть' : 'Включить'}
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => remove.mutate(item.id)}
                      >
                        Удал.
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(draft)}
        title={draft?.id ? 'Изменить аксессуар' : 'Новый аксессуар'}
        onClose={() => setDraft(null)}
      >
        {draft && (
          <div className="space-y-4">
            <LocalizedInput
              label="Название"
              value={draft.name}
              onChange={(name) => setDraft({ ...draft, name })}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Цена, сум">
                <Input
                  value={draft.price}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                />
              </Field>

              <Field label="Тарификация">
                <Select
                  value={draft.priceMode}
                  onChange={(e) =>
                    setDraft({ ...draft, priceMode: e.target.value as Draft['priceMode'] })
                  }
                >
                  <option value="PER_DAY">За сутки</option>
                  <option value="FLAT">За весь период</option>
                </Select>
              </Field>

              <Field label="Залог, сум" hint="Пусто — залог не берётся">
                <Input
                  value={draft.depositAmount}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, depositAmount: e.target.value })}
                />
              </Field>

              <Field label="Остаток, шт">
                <Input
                  value={draft.quantityTotal}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, quantityTotal: e.target.value })}
                />
              </Field>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDraft(null)}>
                Отмена
              </Button>
              <Button disabled={save.isPending} onClick={() => save.mutate(draft)}>
                {save.isPending ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
